/**
 * My Requests View
 *
 * Displays all requests created by the current user with filtering,
 * sorting, search, and pagination.
 */

import { renderIcon } from '../utils/icons';
import { createRequestList, type RequestListFilters, type RequestStatus } from '../components/request-list';
import { createSidebarLayout } from '../components/sidebar-layout';
import { exportRequestsToCSV } from '../utils/csv-export';
import { showToast } from '../components/toast';
import type { ExpenditureRequest } from '../components/request-list';

/**
 * Parse URL query parameters to extract initial filters
 */
function getInitialFiltersFromUrl(): RequestListFilters {
  const params = new URLSearchParams(window.location.search);
  const filters: RequestListFilters = {
    mine: true, // Always show only the current user's requests
  };

  // Parse status filter from URL
  const status = params.get('status');
  if (status && ['DRAFT', 'PENDING', 'APPROVED', 'REJECTED', 'CANCELLED'].includes(status)) {
    filters.status = status as RequestStatus;
  }

  return filters;
}

/**
 * Create the My Requests view
 */
export function createMyRequestsView(): HTMLElement {
  // Create main content
  const content = document.createElement('div');
  content.className = 'px-4 lg:px-6 py-6';

  // Page header with new request button
  const pageHeader = document.createElement('div');
  pageHeader.className = 'flex items-center justify-between mb-6';
  pageHeader.innerHTML = `
    <div>
      <p class="text-muted-foreground">View and manage your expenditure requests</p>
    </div>
    <a href="/requests/new" id="new-request-btn" class="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground font-medium rounded-lg hover:bg-primary/90 transition-colors">
      ${renderIcon('plus', { size: 'sm' }).outerHTML}
      <span>New Request</span>
    </a>
  `;

  // Add click handler for new request button
  const newRequestBtn = pageHeader.querySelector('#new-request-btn');
  if (newRequestBtn) {
    newRequestBtn.addEventListener('click', (e) => {
      e.preventDefault();
      window.history.pushState({}, '', '/requests/new');
      window.dispatchEvent(new PopStateEvent('popstate'));
    });
  }

  // Request list container
  const requestListContainer = document.createElement('div');
  requestListContainer.id = 'request-list-container';

  content.appendChild(pageHeader);
  content.appendChild(requestListContainer);

  // Get initial filters from URL query parameters (e.g., ?status=PENDING)
  const initialFilters = getInitialFiltersFromUrl();

  // Initialize request list after DOM is ready
  // Use mine=true filter to show only the current user's requests (even for admin users)
  setTimeout(() => {
    let allRequests: ExpenditureRequest[] = [];

    const requestList = createRequestList({
      parent: requestListContainer,
      title: 'My Requests',
      subtitle: 'Requests you have created',
      endpoint: '/requests',
      showFilters: true,
      showSearch: true,
      showStatusColumn: true,
      showCreatorColumn: false,
      emptyMessage: 'You have not created any requests yet. Click "New Request" to get started.',
      initialFilters,
      enableBulkSelection: true,
      showBulkActions: true,
      onBulkExport: (ids: string[]) => {
        // Filter requests by selected IDs
        const selectedRequests = allRequests.filter(r => ids.includes(r.id));
        exportRequestsToCSV(selectedRequests);
        showToast({
          type: 'success',
          message: `Exported ${selectedRequests.length} request${selectedRequests.length > 1 ? 's' : ''} to CSV`,
        });
      },
      viewId: 'my-requests',
      showColumnPicker: true,
    });

    // Store all requests for export functionality
    // We'll need to hook into the request list's loaded data
    const originalMethod = Object.getPrototypeOf(requestList).loadRequests;
    if (originalMethod) {
      Object.getPrototypeOf(requestList).loadRequests = async function(...args: any[]) {
        const result = await originalMethod.apply(this, args);
        allRequests = this.allRequests.length > 0 ? this.allRequests : this.requests;
        return result;
      };
    }
  }, 0);

  // Wrap with sidebar layout
  return createSidebarLayout(content, {
    currentPath: '/my-requests',
    title: 'My Requests',
  });
}

/**
 * My Requests view function for router
 */
export function myRequestsView(): HTMLElement {
  return createMyRequestsView();
}
